import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useRef, useState } from 'react';
import { siftActivities } from './sift-activities';
import { searchError } from '../utils/TrackAnalytics';
import { useSearch } from '../SearchForm/use-search';
import { getLocaleActivityKeywords } from '../../api/api-env';

export const useActivityInput = (props) => {
  const {
    defaultActivity,
    value,
    setValue,
    setActiveInput,
    activeInput,
    setValidator,
    noHighlight
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [activitiesList, setActivitiesList] = useState([]);
  const [isOptionError, setIsOptionError] = useState(false);
  const [inputDisplay, setInputDisplay] = useState(value?.label || '');
  const [inputValue, setInputValue] = useState('');
  const [interimOption, setInterimOption] = useState('');

  const isMounted = useRef(false);
  const { origin, intl } = useSearch();

  useEffect(() => {
    (async () => {
      try {
        // get the full list of activities on mount
        const activityKeywords = await getLocaleActivityKeywords(
          intl.locale
        );
        // prevent potentially memory leak
        if (isMounted.current) {
          setActivitiesList(activityKeywords);
        }
      } catch (error) {
        console.error('ActivityInput: error fetching activity keywords', error);
      }
    })();
  }, []);

  useEffect(() => {
    if (!defaultActivity || !activitiesList.length) {
      return;
    }
    (async () => {
      try {
        // if a defaultActivity was provided, find it in the activities list
        // and set the initial value for the input
        const siftedActivities = await siftActivities(
          activitiesList,
          defaultActivity
        );
        const exactMatch = find(siftedActivities, [
          'value.dropdown',
          defaultActivity
        ]);
        if (exactMatch) {
          setValue(exactMatch, false);
          setDefaultOptions(siftedActivities);
        }
      } catch (error) {
        console.error('ActivityInput: error sifting activities', error);
        setValue({}, false);
        setDefaultOptions([]);
      }
    })();
  }, [activitiesList]);

  useEffect(() => {
    setInputDisplay((value || {}).label || '');
  }, [value]);

  useEffect(() => {
    if (isOptionError) {
      searchError(inputValue, origin, 'activity')
    }
  }, [isOptionError]);

  const validate = useCallback(() => {
    let isValid = true;
    const hasValue = !(isEmpty(value) || isEmpty(value.value));
    if (!hasValue) {
      isValid = false;
      focus();
    }
    return isValid;
  }, [value]);

  useEffect(() => {
    if (typeof setValidator === 'function') {
      setValidator(validate);
    }
  }, [setValidator, validate]);

  const loadOptions = (inputValue, callback) => {
    setInputValue(inputValue);
    setTimeout(async () => {
      try {
        const siftedActivites = await siftActivities(
          activitiesList,
          inputValue
        );
        setIsOptionError(false);
        callback(siftedActivites);
      } catch (e) {
        setIsOptionError(true);
      }
    }, 0);
  };

  const onChange = (value, { action }, shouldBlur = true, callback) => {
    if (!value && 'clear' === action) {
      setDefaultOptions([]);
      setValue({});
    }

    if (value && 'select-option' === action) {
      (async () => {
        const siftedActivites = await siftActivities(
          activitiesList,
          value.label
        );
        if (shouldBlur) {
          blur();
        }
        PSSessions.Cookies.set('PSSK', value.label, Date.now() + 3888000000);
        PSSessions.Cookies.set('PSSKT', 'input', Date.now() + 3888000000);
        setDefaultOptions(siftedActivites);
        setValue(value);
        if (callback) {
          setInterimOption('');
          return callback();
        }
      })();
    }
  };

  const inputChange = (value, { action }) => {
    if (!value && action === 'input-change') {
      setDefaultOptions([]);
      setValue({});
    }
    if (action === 'input-change') {
      setInputDisplay(value || '');
      if (value) {
        (async () => {
          try {
            const siftedActivites = await siftActivities(
              activitiesList,
              value
            ) || [];
            if (siftedActivites.length > 0 ) setInterimOption(siftedActivites[0]);
          } catch (e) {
            setInterimOption('');
          }
        })();
      }
    }
  };

  const handleInputFocus = (e) => {
    if (e) {
      e.preventDefault();
      if (!noHighlight) {
        e.target.setSelectionRange(0, e.target.value.length);
      }
    }
    setIsOptionError(false);
    setIsFocused(true);
    if (typeof setActiveInput === 'function') {
      setActiveInput('activity');
    }
  };

  const handleInputBlur = (e) => {
    if (e) {
      e.preventDefault();
    }
    blur();
  };

  const handleContainerFocus = () => {
    setIsFocused(true);
    if (typeof setActiveInput === 'function') {
      setActiveInput('activity');
    }
  };

  const focus = () => {
    handleContainerFocus();
    handleInputFocus();
  };

  const blur = () => {
    if (inputDisplay && interimOption) {
      setValue(interimOption, undefined, true);
    }
    setIsFocused(false);
    if (typeof setActiveInput === 'function') {
      setActiveInput('');
    }
  };

  useEffect(() => {
    // blur on unmount
    return blur;
  }, []);

  useEffect(() => {
    // activeInput is sometimes set by SearchForm
    if (activeInput === 'activity' && !isFocused) {
      focus();
    }
  }, [activeInput]);

  useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);

  return {
    loadOptions,
    onChange,
    inputChange,
    handleInputFocus,
    handleInputBlur,
    handleContainerFocus,
    focus,
    blur,
    validate,
    defaultOptions,
    isOptionError,
    activitiesList,
    isFocused,
    inputDisplay,
    ...props
  };
};
